import { FormDialogMode, useNotify } from '@globaleris/ui.library'
import { Grid } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoAdhesionCFlotante, EnumTipoConceptoFlotante } from '../../../api'
import {
  FloatingConceptsDataTableModel,
  FloatingConceptsModel
} from '../../../api/Argentia/endpoints/floatingConcepts/model/floatingConcepts'
import { enumValueToKey } from '../../../helpers'
import { formatDateOnlyString } from '../../../helpers/formatDateOnly'
import { getResourceUserPermissions } from '../../../helpers/permissionsHelper'
import { useConfirmationDialog, usePageState } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { Resources } from '../../../model/RBAC/constants'
import { generateFloatingConceptsInvoice } from '../../invoicesPage/invoicesPage.service'
import {
  mapFloatingConceptFormDataToPatchRequest,
  mapFloatingConceptsDataTableToState,
  mapFloatingConceptsToGenerateInvoiceRequest
} from '../floatingConcepts.mapper'
import { EnumFloatingConceptsTabs } from '../floatingConcepts.model'
import {
  deleteFloatingConcept,
  deleteFloatingConceptsMassive,
  fetchFloatingConcepts,
  fetchFloatingConceptsByContractID,
  fetchRightsFloatingConcepts,
  fetchRightsFloatingConceptsByContractID,
  patchFloatingConcept
} from '../floatingConcepts.service'
import { FloatingConceptsDataTable } from './floatingConceptsDataTable/floatingConcepts.DataTable'
import { FloatingConceptsFormDialog } from './floatingConceptsEditFormDialog/floatingConceptsFormDialog'
import { FloatingConceptsSearchForm } from './searchForm/floatingConcepts.searchForm'
import {
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../redux'

type FloatingConceptsDefaultTabProps = {
  activeTab: EnumFloatingConceptsTabs
  commodity: EnumCommodity
  onSelectCommodity: (commodity: EnumCommodity) => void
}

interface FormDependenciesProps {
  formState: any
  amountError: boolean
}

// Converter function from EnumFloatingConceptsTabs to EnumTipoConceptoFlotante
const mapTabToConceptoType = (tab: EnumFloatingConceptsTabs): EnumTipoConceptoFlotante => {
  switch (tab) {
    case EnumFloatingConceptsTabs.distribucion:
      return EnumTipoConceptoFlotante.Distribucion
    case EnumFloatingConceptsTabs.refacturacion:
      return EnumTipoConceptoFlotante.Refacturacion
    case EnumFloatingConceptsTabs.penalizacion:
      return EnumTipoConceptoFlotante.Penalizacion
    case EnumFloatingConceptsTabs.manual:
      return EnumTipoConceptoFlotante.Manual
    case EnumFloatingConceptsTabs.virtualBattery:
      return EnumTipoConceptoFlotante.BateriaVirtual
    case EnumFloatingConceptsTabs.balanceActions:
      return EnumTipoConceptoFlotante.AccionBalance
    default:
      return EnumTipoConceptoFlotante.Manual
  }
}

export const FloatingConceptsDefaultTab: React.FC<FloatingConceptsDefaultTabProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'floatingConcepts'])
  const history = useHistory()
  const { dataTableFilter, dataTableSearch } = usePageState()
  const { info, error } = useNotify()
  const { getInformation, getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<FormDialogMode>(FormDialogMode.ADD)
  const [formInputData, setFormInputData] = useState<FloatingConceptsDataTableModel>(
    {} as FloatingConceptsDataTableModel
  )
  const [floatingConcepts, setFloatingConcepts] = useState<Partial<FloatingConceptsModel>[]>([])
  const [floatingConceptsDataTable, setFloatingConceptsDataTable] = useState<
    FloatingConceptsDataTableModel[]
  >([])
  const [searchForm, setSearchForm] = useState<FloatingConceptsSearchForm>(
    {} as FloatingConceptsSearchForm
  )
  const [formHasValue, setFormHasValue] = useState<boolean>(false)
  const [isFetchingTable, setIsFetchingTable] = useState<boolean>(false)
  const [last1000FilterIsActive, setLast1000FilterIsActive] = useState<boolean>(false)

  const activeTab = props.activeTab
  // const isRightsTab = props.activeTab === EnumFloatingConceptsTabs.rights
  const commodity = props.commodity

  // #region Handlers Starts

  // ------------------- DataTable Action Handlers -----------------------------//

  const onAddFloatingConceptClickHandler = async (
    formData: FloatingConceptsDataTableModel | undefined
  ) => {
    history.push(NonAuthRoutes.floatingConceptsAdd, {
      activeTab: activeTab,
      floatingConceptType: EnumTipoConceptoFlotante.Manual
      // ...(isRightsTab && { adhesionType: EnumTipoAdhesionCFlotante.Derechos })
    })
  }

  const onEditFloatingConceptClickHandler = async (conceptData: FloatingConceptsDataTableModel) => {
    setFormMode(FormDialogMode.EDIT)
    setFormDialogOpen(true)
    setFormInputData(conceptData)
  }

  // ------------------- Form Dialog Handlers -----------------------------//
  const handleFormDialogClose = (formData: any) => {
    if (formData) {
      if (formMode === FormDialogMode.EDIT) {
        const request = mapFloatingConceptFormDataToPatchRequest(formData)
        patchFloatingConcept(request).then(async response => {
          if (response.success) {
            updateFloatingConceptsTable()
            info(t('floatingConcepts:notifications.editFloatingConcept.success'))
          } else {
            error(t('floatingConcepts:notifications.editFloatingConcept.error'))
            return
          }
        })
      }
    }
    setFormDialogOpen(false)
  }

  const onDeleteFloatingConceptClickHandler = (
    rowFloatingConcept: FloatingConceptsDataTableModel
  ) => {
    getConfirmation(ConfirmationDialogs.DELETE_PRICEVALUE).then(async () => {
      deleteFloatingConcept(rowFloatingConcept.conceptId!).then(async response => {
        if (response) {
          updateFloatingConceptsTable()
          info(t('floatingConcepts:notifications.deleteFloatingConcept.success'))
        } else {
          error(t('floatingConcepts:notifications.deleteFloatingConcept.error'))
        }
      })
      closeConfirmation()
    })
  }

  const onDeleteSelectedFloatingConceptsClickHandler = (
    selectedRows: FloatingConceptsDataTableModel[]
  ) => {
    if (selectedRows.length === 0) {
      return
    }
    getConfirmation(
      ConfirmationDialogs.DELETE_FLOATING_CONCEPTS,
      t(
        `confirmDialogs:${enumValueToKey(ConfirmationDialogs, ConfirmationDialogs.DELETE_FLOATING_CONCEPTS)}.message`,
        { count: selectedRows.length }
      )
    ).then(async () => {
      closeConfirmation()
      const selectedIds = selectedRows.map(row => row.conceptId!)
      const response = await deleteFloatingConceptsMassive(selectedIds)
      if (response.success) {
        setFloatingConceptsDataTable(prev =>
          prev.filter(row => !selectedIds.includes(row.conceptId!))
        )
        info(t('floatingConcepts:notifications.deleteFloatingConcepts.success'))
      } else {
        error(t('floatingConcepts:notifications.deleteFloatingConcepts.error'))
      }
    })
  }

  // this function provides the fetchingData fx to handle defaultTab and rightsTab
  const fetchByContractID = async (
    contractId: number,
    commodity: EnumCommodity,
    tipoConcepto: EnumTipoConceptoFlotante,
    startDate: string | undefined,
    endDate: string | undefined
  ) => {
    // if (isRightsTab) {
    //   return await fetchRightsFloatingConceptsByContractID(contractId, startDate, endDate)
    // }
    return await fetchFloatingConceptsByContractID(
      contractId,
      commodity,
      tipoConcepto,
      startDate,
      endDate
    )
  }

  const handleSearchAllButton = async (searchFormData: FloatingConceptsSearchForm) => {
    setLast1000FilterIsActive(false)
    setIsFetchingTable(true)
    setSearchForm(searchFormData)
    const conceptoType = mapTabToConceptoType(activeTab)
    const commodityKey = enumValueToKey(EnumCommodity, commodity)
    const tipoConceptoKey = enumValueToKey(EnumTipoConceptoFlotante, conceptoType)

    await fetchByContractID(
      searchFormData.contract!,
      commodityKey,
      tipoConceptoKey,
      formatDateOnlyString(searchFormData.startDate),
      formatDateOnlyString(searchFormData.endDate)
    )
      .then(response => {
        if (response.success && response.data) {
          setFloatingConcepts(mapFloatingConceptsDataTableToState(response.data)) // TableModel omits expanded related entities like Contract or SettlementGroup (except for related entities Id) to improve performance
          setFloatingConceptsDataTable(response.data)
        } else {
          error(t('floatingConcepts:notifications.loadFloatingConcepts.error'))
        }
      })
      .finally(() => {
        setIsFetchingTable(false)
      })
  }

  const handleFilterLastButton = () => {
    setLast1000FilterIsActive(true)
    updateFloatingConceptsTable()
  }

  // this function provides the fetchingData fx to handle defaultTab and rightsTab
  const fetchFloatingConceptsType = async (tabType: EnumFloatingConceptsTabs) => {
    const conceptoType = mapTabToConceptoType(tabType)
    const commodityKey = enumValueToKey(EnumCommodity, commodity)
    const tipoConceptoKey = enumValueToKey(EnumTipoConceptoFlotante, conceptoType)

    return await fetchFloatingConcepts(commodityKey, tipoConceptoKey)
  }

  const updateFloatingConceptsTable = async () => {
    setIsFetchingTable(true)
    await fetchFloatingConceptsType(activeTab)
      .then(response => {
        if (response.success && response.data) {
          setFloatingConcepts(mapFloatingConceptsDataTableToState(response.data)) // TableModel omits expanded related entities like Contract or SettlementGroup (except for related entities Id) to improve performance
          setFloatingConceptsDataTable(response.data)
        } else {
          error(t('floatingConcepts:notifications.loadFloatingConcepts.error'))
        }
      })
      .finally(() => {
        setIsFetchingTable(false)
      })
  }

  const handleFormClear = () => {
    setFormHasValue(false)
    setFloatingConceptsDataTable([])
  }

  const handleFormHasValue = (hasValue: boolean) => {
    setFormHasValue(hasValue)
  }

  // #endregion Handlers Ends

  // #region Functions Starts

  const checkHasSameContractId = (selectedRows: FloatingConceptsDataTableModel[]) => {
    if (selectedRows.length > 0) {
      const contractId = selectedRows[0].contractId
      return selectedRows.every(concept => concept.contractId === contractId)
    }
  }

  const generateInvoice = (selectedRows: FloatingConceptsDataTableModel[]) => {
    if (selectedRows.length > 0) {
      getConfirmation(
        ConfirmationDialogs.GENERATE_INVOICE,
        t(
          `confirmDialogs:${enumValueToKey(ConfirmationDialogs, ConfirmationDialogs.GENERATE_INVOICE)}.message`,
          { count: selectedRows.length }
        )
      ).then(async () => {
        const request = mapFloatingConceptsToGenerateInvoiceRequest(selectedRows)
        if (request.concepts.some(concept => concept === undefined)) {
          error(t('floatingConcepts:notifications.generateInvoiceConcepts.incompleted'))
        } else {
          await generateFloatingConceptsInvoice(request)
            .then(response => {
              if (response.success) {
                setIsFetchingTable(true)
                info(t('floatingConcepts:notifications.generateInvoiceConcepts.success'))
                const selectedIds = selectedRows.map(row => row.conceptId)
                setFloatingConceptsDataTable(prev =>
                  prev.filter(row => !selectedIds.includes(row.conceptId))
                )
                setIsFetchingTable(false)
              } else {
                error(t('floatingConcepts:notifications.generateInvoiceConcepts.error'))
              }
            })
            .finally(() => {
              closeConfirmation()
            })
        }
      })
    }
  }

  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // update FloatingConceptsTable when tab or commodity changes and clear filters
  useEffect(() => {
    storeDataTableFilterForCurrentPage([])
    storeDataTableSearchForCurrentPage('')
    updateFloatingConceptsTable()
  }, [activeTab, commodity])
  // #endregion Hooks Ends

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FloatingConceptsSearchForm
            activeTab={activeTab}
            commodity={commodity}
            onFormClear={handleFormClear}
            onFormHasValue={handleFormHasValue}
            onSearchAllButton={handleSearchAllButton}
            onFilterLastButton={handleFilterLastButton}
          />
        </Grid>
        <Grid item xs={12}>
          <FloatingConceptsDataTable
            activeTab={activeTab}
            isFetchingTable={isFetchingTable}
            floatingConceptsDataTable={floatingConceptsDataTable}
            userPermissions={getResourceUserPermissions(Resources.FloatingConcepts)}
            dataTableFilter={dataTableFilter}
            dataTableSearch={dataTableSearch}
            onAddFloatingConceptClick={onAddFloatingConceptClickHandler}
            onEditFloatingConceptClick={onEditFloatingConceptClickHandler}
            onDeleteFloatingConceptClick={onDeleteFloatingConceptClickHandler}
            onGenerateInvoiceClick={generateInvoice}
            onDeleteSelectedFloatingConceptsClick={onDeleteSelectedFloatingConceptsClickHandler}
          />
        </Grid>
      </Grid>
      <FloatingConceptsFormDialog
        activeTab={activeTab}
        formMode={formMode}
        open={formDialogOpen}
        onClose={handleFormDialogClose}
        inputData={formInputData}
      />
    </>
  )
}
